import * as THREE from "https://esm.sh/three@0.166.1";
import { OrbitControls } from "https://esm.sh/three@0.166.1/examples/jsm/controls/OrbitControls.js";
import { geoEquirectangular, geoPath } from "https://esm.sh/d3-geo@3.1.1";
import { feature as topojsonFeature, mesh as topojsonMesh } from "https://esm.sh/topojson-client@3.1.0";

const DEFAULT_LOGO_URL = "https://ombrenoir.ca/cdn/shop/files/logogo_4f7c0ddb-50e2-4ee4-af7f-3a4385340da3.png";
const EARTH_TEXTURE_URL = "https://threejs.org/examples/textures/planets/earth_atmos_2048.jpg";
const WORLD_ATLAS_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const PURCHASE_MESSAGE = "Someone placed an order here.";
const CITY_LIGHT_COUNT = 2600;
const INITIAL_CENTER_LONGITUDE = -90;
const LOGO_DECAL_LATITUDE = 0;
const LAND_DOT_BUFFER_DEGREES = 0.7;
const MIN_LAND_GOLD_INTENSITY = 0.52;
const HIGHLIGHT_ROTATION_MS = 4500;
const ACTIVE_PURCHASE_PULSE_COUNT = 3;
const VISIBLE_PURCHASE_DOT_THRESHOLD = 0.12;
const MIN_HIGHLIGHT_SEPARATION_DEGREES = 38;
const RECENT_HIGHLIGHT_MEMORY = 8;
const PURCHASE_DOT_SEPARATION_DEGREES = 6;
const PURCHASE_LOCATION_MATCH_PRECISION = 4;
const PURCHASE_LOCATION_ROTATION_STEP = 7;

const COUNTRIES = [
  { code: "CA", name: "Canada", city: "Toronto", lat: 43.6532, lon: -79.3832 },
  { code: "US", name: "United States", city: "New York", lat: 40.7128, lon: -74.006 },
  { code: "GB", name: "United Kingdom", city: "London", lat: 51.5072, lon: -0.1276 },
  { code: "FR", name: "France", city: "Paris", lat: 48.8566, lon: 2.3522 },
  { code: "JP", name: "Japan", city: "Tokyo", lat: 35.6762, lon: 139.6503 },
  { code: "AU", name: "Australia", city: "Sydney", lat: -33.8688, lon: 151.2093 },
  { code: "BR", name: "Brazil", city: "Sao Paulo", lat: -23.5558, lon: -46.6396 },
  { code: "AE", name: "United Arab Emirates", city: "Dubai", lat: 25.2048, lon: 55.2708 },
  { code: "DE", name: "Germany", city: "Berlin", lat: 52.52, lon: 13.405 },
  { code: "SG", name: "Singapore", city: "Singapore", lat: 1.3521, lon: 103.8198 },
  { code: "ZA", name: "South Africa", city: "Cape Town", lat: -33.9249, lon: 18.4241 },
  { code: "MX", name: "Mexico", city: "Mexico City", lat: 19.4326, lon: -99.1332 },
];

const LOCATIONS = [
  ...COUNTRIES,
  { code: "US", name: "United States", city: "Los Angeles", lat: 34.0522, lon: -118.2437 },
  { code: "US", name: "United States", city: "Chicago", lat: 41.8781, lon: -87.6298 },
  { code: "US", name: "United States", city: "Miami", lat: 25.7617, lon: -80.1918 },
  { code: "US", name: "United States", city: "Seattle", lat: 47.6061, lon: -122.3328 },
  { code: "CA", name: "Canada", city: "Vancouver", lat: 49.2827, lon: -123.1207 },
  { code: "CA", name: "Canada", city: "Montreal", lat: 45.5019, lon: -73.5674 },
  { code: "GB", name: "United Kingdom", city: "Manchester", lat: 53.4808, lon: -2.2426 },
  { code: "FR", name: "France", city: "Lyon", lat: 45.764, lon: 4.8357 },
  { code: "JP", name: "Japan", city: "Osaka", lat: 34.6937, lon: 135.5023 },
  { code: "AU", name: "Australia", city: "Melbourne", lat: -37.8136, lon: 144.9631 },
];

const VERIFIED_PURCHASE_LOCATION_KEYS = new Set(LOCATIONS.map(purchaseLocationKey));
const instances = new WeakMap();

function toTextureAlignedVector(lat, lon, radius = 1) {
  const latRad = (lat * Math.PI) / 180;
  const lonRad = (lon * Math.PI) / 180;
  const radiusAtLat = Math.cos(latRad);

  return new THREE.Vector3(
    radiusAtLat * Math.cos(lonRad) * radius,
    Math.sin(latRad) * radius,
    -radiusAtLat * Math.sin(lonRad) * radius,
  );
}

function toThreeVector(lat, lon, radius = 1) {
  return toTextureAlignedVector(lat, lon, radius);
}

function readNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.addEventListener("load", () => resolve(image), { once: true });
    image.addEventListener("error", reject, { once: true });
    image.src = url;
  });
}

function clampChannel(value) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function createBlackGoldEarthPixel(red, green, blue, alpha = 255) {
  const brightest = Math.max(red, green, blue);
  const darkest = Math.min(red, green, blue);
  const brightness = (red + green + blue) / 3;
  const isCloudOrIce = brightness > 176 && brightest - darkest < 64;
  const isOcean = blue > red * 1.16 && blue > green * 1.03;
  const goldIntensity = Math.max(MIN_LAND_GOLD_INTENSITY, Math.min(1, brightness / 185));

  if (isCloudOrIce) {
    return [
      clampChannel(70 + goldIntensity * 82),
      clampChannel(48 + goldIntensity * 74),
      clampChannel(8 + goldIntensity * 32),
      alpha,
    ];
  }

  if (isOcean) {
    return [
      clampChannel(red * 0.045 + 1),
      clampChannel(green * 0.05 + 2),
      clampChannel(blue * 0.065 + 4),
      alpha,
    ];
  }

  return [
    clampChannel(104 + goldIntensity * 136),
    clampChannel(72 + goldIntensity * 112),
    clampChannel(10 + goldIntensity * 45),
    alpha,
  ];
}

async function createDarkEarthTexture() {
  const image = await loadImage(EARTH_TEXTURE_URL);
  const textureCanvas = document.createElement("canvas");
  textureCanvas.width = image.naturalWidth || image.width || 2048;
  textureCanvas.height = image.naturalHeight || image.height || 1024;

  const context = textureCanvas.getContext("2d", { willReadFrequently: true });
  context.drawImage(image, 0, 0, textureCanvas.width, textureCanvas.height);

  const imageData = context.getImageData(0, 0, textureCanvas.width, textureCanvas.height);
  const data = imageData.data;

  for (let index = 0; index < data.length; index += 4) {
    const [red, green, blue, alpha] = createBlackGoldEarthPixel(
      data[index],
      data[index + 1],
      data[index + 2],
      data[index + 3],
    );

    data[index] = red;
    data[index + 1] = green;
    data[index + 2] = blue;
    data[index + 3] = alpha;
  }

  context.putImageData(imageData, 0, 0);

  const texture = new THREE.CanvasTexture(textureCanvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  texture.needsUpdate = true;

  return texture;
}

async function createLogoTexture(logoUrl) {
  const image = await loadImage(logoUrl || DEFAULT_LOGO_URL);
  const textureCanvas = document.createElement("canvas");
  textureCanvas.width = 1024;
  textureCanvas.height = 512;

  const context = textureCanvas.getContext("2d");
  context.clearRect(0, 0, textureCanvas.width, textureCanvas.height);
  context.drawImage(image, 0, 0, textureCanvas.width, textureCanvas.height);

  const imageData = context.getImageData(0, 0, textureCanvas.width, textureCanvas.height);
  const data = imageData.data;

  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3];

    if (alpha > 4) {
      data[index] = 255;
      data[index + 1] = 255;
      data[index + 2] = 255;
      data[index + 3] = 255;
    }
  }

  context.putImageData(imageData, 0, 0);

  const texture = new THREE.CanvasTexture(textureCanvas);
  texture.colorSpace = THREE.SRGBColorSpace;

  return texture;
}

function createGlobeRimGlow() {
  const rimGlow = new THREE.Group();

  const glowShell = new THREE.Mesh(
    new THREE.SphereGeometry(2.125, 128, 128),
    new THREE.ShaderMaterial({
      uniforms: {
        glowColor: { value: new THREE.Color(0xffc84a) },
        rimPower: { value: 4.6 },
        glowStrength: { value: 0.58 },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vViewPosition;

        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          vNormal = normalize(normalMatrix * normal);
          vViewPosition = normalize(-mvPosition.xyz);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 glowColor;
        uniform float rimPower;
        uniform float glowStrength;
        varying vec3 vNormal;
        varying vec3 vViewPosition;

        void main() {
          float rim = 1.0 - abs(dot(normalize(vNormal), normalize(vViewPosition)));
          float glow = smoothstep(0.72, 1.0, pow(rim, rimPower)) * glowStrength;
          gl_FragColor = vec4(glowColor, glow);
        }
      `,
      transparent: true,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
    }),
  );

  rimGlow.add(glowShell);

  return rimGlow;
}

function isDatelineJump(previous, next) {
  return Math.abs(Number(next[0]) - Number(previous[0])) > 180;
}

function createSeededRandom(seed = 1) {
  let state = seed >>> 0;

  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function forEachCoordinate(coordinates, callback) {
  if (typeof coordinates?.[0] === "number") {
    callback(coordinates);
    return;
  }

  for (const coordinate of coordinates || []) {
    forEachCoordinate(coordinate, callback);
  }
}

function bboxForGeometry(geometry) {
  const bbox = {
    minLon: Infinity,
    maxLon: -Infinity,
    minLat: Infinity,
    maxLat: -Infinity,
  };

  forEachCoordinate(geometry.coordinates, ([lon, lat]) => {
    bbox.minLon = Math.min(bbox.minLon, lon);
    bbox.maxLon = Math.max(bbox.maxLon, lon);
    bbox.minLat = Math.min(bbox.minLat, lat);
    bbox.maxLat = Math.max(bbox.maxLat, lat);
  });

  return bbox;
}

function pointInRing(lon, lat, ring) {
  let inside = false;

  for (let index = 0, previousIndex = ring.length - 1; index < ring.length; previousIndex = index, index += 1) {
    const [currentLon, currentLat] = ring[index];
    const [previousLon, previousLat] = ring[previousIndex];
    const intersects =
      currentLat > lat !== previousLat > lat &&
      lon < ((previousLon - currentLon) * (lat - currentLat)) / (previousLat - currentLat || 1) + currentLon;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
}

function pointInPolygon(lon, lat, polygon) {
  if (!pointInRing(lon, lat, polygon[0] || [])) {
    return false;
  }

  for (let index = 1; index < polygon.length; index += 1) {
    if (pointInRing(lon, lat, polygon[index])) {
      return false;
    }
  }

  return true;
}

function pointInGeometry(lon, lat, geometry) {
  if (geometry.type === "Polygon") {
    return pointInPolygon(lon, lat, geometry.coordinates);
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.some((polygon) => pointInPolygon(lon, lat, polygon));
  }

  return false;
}

function normalizeLongitude(lon) {
  return ((lon + 540) % 360) - 180;
}

function pointHasLandBuffer(lon, lat, geometry) {
  const latRadians = (lat * Math.PI) / 180;
  const latBuffer = LAND_DOT_BUFFER_DEGREES;
  const lonBuffer = LAND_DOT_BUFFER_DEGREES / Math.max(0.35, Math.cos(latRadians));
  const diagonalLon = lonBuffer * 0.72;
  const diagonalLat = latBuffer * 0.72;
  const samples = [
    [0, 0],
    [lonBuffer, 0],
    [-lonBuffer, 0],
    [0, latBuffer],
    [0, -latBuffer],
    [diagonalLon, diagonalLat],
    [-diagonalLon, diagonalLat],
    [diagonalLon, -diagonalLat],
    [-diagonalLon, -diagonalLat],
  ];

  return samples.every(([deltaLon, deltaLat]) => {
    return pointInGeometry(normalizeLongitude(lon + deltaLon), lat + deltaLat, geometry);
  });
}

function createMetallicGoldGradient(context, textureCanvas) {
  const gradient = context.createLinearGradient(0, 0, textureCanvas.width, textureCanvas.height);

  gradient.addColorStop(0, "rgba(112, 72, 10, 0.98)");
  gradient.addColorStop(0.2, "rgba(207, 150, 30, 0.98)");
  gradient.addColorStop(0.42, "rgba(255, 225, 114, 0.98)");
  gradient.addColorStop(0.58, "rgba(178, 120, 18, 0.98)");
  gradient.addColorStop(0.78, "rgba(244, 194, 68, 0.98)");
  gradient.addColorStop(1, "rgba(132, 86, 14, 0.98)");

  return gradient;
}

function createCountryFillTexture(countriesGeojson) {
  const textureCanvas = document.createElement("canvas");
  textureCanvas.width = 2048;
  textureCanvas.height = 1024;

  const context = textureCanvas.getContext("2d");
  const projection = geoEquirectangular()
    .translate([textureCanvas.width / 2, textureCanvas.height / 2])
    .scale(textureCanvas.width / (2 * Math.PI))
    .precision(0.2);
  const path = geoPath(projection, context);

  context.clearRect(0, 0, textureCanvas.width, textureCanvas.height);
  context.beginPath();
  path(countriesGeojson);
  context.fillStyle = createMetallicGoldGradient(context, textureCanvas);
  context.fill("evenodd");
  context.save();
  context.clip("evenodd");
  context.globalCompositeOperation = "screen";
  context.fillStyle = "rgba(255, 238, 166, 0.15)";

  for (let y = -textureCanvas.height; y < textureCanvas.height * 1.25; y += 88) {
    context.save();
    context.translate(textureCanvas.width / 2, y);
    context.rotate(-0.22);
    context.fillRect(-textureCanvas.width, -7, textureCanvas.width * 2, 14);
    context.restore();
  }

  context.restore();
  context.globalCompositeOperation = "source-over";
  context.strokeStyle = "rgba(255, 230, 128, 0.38)";
  context.lineWidth = 0.75;
  context.stroke();

  const texture = new THREE.CanvasTexture(textureCanvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  texture.needsUpdate = true;

  return texture;
}

function createCountryFillGroup(countriesGeojson) {
  const fillTexture = createCountryFillTexture(countriesGeojson);
  const landFill = new THREE.Mesh(
    new THREE.SphereGeometry(2.088, 128, 128),
    new THREE.MeshStandardMaterial({
      map: fillTexture,
      transparent: true,
      opacity: 0.98,
      alphaTest: 0.01,
      metalness: 0.72,
      roughness: 0.24,
      emissive: 0x221400,
      emissiveIntensity: 0.04,
      depthTest: true,
      depthWrite: false,
    }),
  );
  landFill.renderOrder = 1;

  const group = new THREE.Group();
  group.add(landFill);
  group.userData.hasTexture = true;

  return group;
}

function createCityLightField(countriesGeojson, count = CITY_LIGHT_COUNT) {
  const random = createSeededRandom(7331);
  const candidates = countriesGeojson.features
    .map((country) => {
      const bbox = bboxForGeometry(country.geometry);
      const width = Math.max(0, bbox.maxLon - bbox.minLon);
      const height = Math.max(0, bbox.maxLat - bbox.minLat);

      return {
        country,
        bbox,
        weight: Math.max(0.1, Math.sqrt(width * height)),
      };
    })
    .filter(({ bbox }) => Number.isFinite(bbox.minLon) && Number.isFinite(bbox.maxLat));

  const totalWeight = candidates.reduce((sum, item) => sum + item.weight, 0);
  const positions = [];
  const colors = [];
  let attempts = 0;

  while (positions.length / 3 < count && attempts < count * 240) {
    attempts += 1;
    let pick = random() * totalWeight;
    let selected = candidates[0];

    for (const candidate of candidates) {
      pick -= candidate.weight;

      if (pick <= 0) {
        selected = candidate;
        break;
      }
    }

    const { bbox, country } = selected;
    const lon = bbox.minLon + random() * (bbox.maxLon - bbox.minLon);
    const lat = bbox.minLat + random() * (bbox.maxLat - bbox.minLat);

    if (!pointHasLandBuffer(lon, lat, country.geometry)) {
      continue;
    }

    const point = toThreeVector(lat, lon, 2.142 + random() * 0.008);
    const warmth = 0.72 + random() * 0.28;

    positions.push(point.x, point.y, point.z);
    colors.push(0.88 + warmth * 0.12, 0.64 + warmth * 0.2, 0.28 + random() * 0.1);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));

  const lights = new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      size: 0.032,
      vertexColors: true,
      transparent: true,
      opacity: 0.94,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  lights.userData.count = positions.length / 3;
  lights.renderOrder = 3;

  return lights;
}

function createCountryBorderGroup(geojson) {
  const positions = [];
  const radius = 2.097;

  for (const line of geojson.coordinates || []) {
    for (let index = 1; index < line.length; index += 1) {
      const previous = line[index - 1];
      const next = line[index];

      if (isDatelineJump(previous, next)) {
        continue;
      }

      const start = toThreeVector(previous[1], previous[0], radius);
      const end = toThreeVector(next[1], next[0], radius);

      positions.push(start.x, start.y, start.z, end.x, end.y, end.z);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));

  const borders = new THREE.LineSegments(
    geometry,
    new THREE.LineBasicMaterial({
      color: 0xd4af37,
      transparent: true,
      opacity: 0.5,
      depthTest: true,
      depthWrite: false,
    }),
  );
  borders.userData.segmentCount = positions.length / 6;
  borders.renderOrder = 2;

  const group = new THREE.Group();
  group.add(borders);
  group.userData.segmentCount = borders.userData.segmentCount;

  return group;
}

function createLogoDecalGroup(texture, longitude, logoScale) {
  const logoPosition = toThreeVector(LOGO_DECAL_LATITUDE, longitude, 2.245);
  const normal = logoPosition.clone().normalize();
  const worldUp = new THREE.Vector3(0, 1, 0);
  const tangent = new THREE.Vector3().crossVectors(worldUp, normal).normalize();
  const bitangent = new THREE.Vector3().crossVectors(normal, tangent).normalize();
  const rotationMatrix = new THREE.Matrix4().makeBasis(tangent, bitangent, normal);
  const group = new THREE.Group();

  group.position.copy(logoPosition);
  group.quaternion.setFromRotationMatrix(rotationMatrix);

  const logo = new THREE.Mesh(
    new THREE.PlaneGeometry(2.72 * logoScale, 1.36 * logoScale),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 1,
      alphaTest: 0.02,
      side: THREE.FrontSide,
      depthTest: true,
      depthWrite: false,
    }),
  );
  logo.position.z = 0.02;
  logo.renderOrder = 5;

  group.add(logo);

  return group;
}

function placePurchaseDot(group, visitor) {
  const surfacePosition = toThreeVector(visitor.lat, visitor.lon, 2.145);
  const normal = surfacePosition.clone().normalize();

  group.position.copy(surfacePosition);
  group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
  group.userData.id = visitor.id;
  group.userData.countryCode = visitor.countryCode;
  group.userData.lat = visitor.lat;
  group.userData.lon = visitor.lon;
  group.userData.message = visitor.message || PURCHASE_MESSAGE;
}

function createPulseHaloTexture() {
  const textureCanvas = document.createElement("canvas");
  textureCanvas.width = 192;
  textureCanvas.height = 192;

  const context = textureCanvas.getContext("2d");
  const center = textureCanvas.width / 2;
  const gradient = context.createRadialGradient(center, center, 4, center, center, center);
  gradient.addColorStop(0, "rgba(247, 255, 190, 0.94)");
  gradient.addColorStop(0.22, "rgba(255, 216, 95, 0.56)");
  gradient.addColorStop(0.56, "rgba(212, 175, 55, 0.2)");
  gradient.addColorStop(1, "rgba(212, 175, 55, 0)");

  context.fillStyle = gradient;
  context.fillRect(0, 0, textureCanvas.width, textureCanvas.height);

  const texture = new THREE.CanvasTexture(textureCanvas);
  texture.colorSpace = THREE.SRGBColorSpace;

  return texture;
}

function createPurchaseHighlight() {
  const group = new THREE.Group();
  const halo = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: createPulseHaloTexture(),
      color: 0xd4af37,
      transparent: true,
      opacity: 0.56,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
    }),
  );
  halo.position.y = 0.065;
  halo.scale.set(0.42, 0.42, 1);

  const pulse = new THREE.Mesh(
    new THREE.TorusGeometry(0.105, 0.0028, 12, 64),
    new THREE.MeshBasicMaterial({
      color: 0xd4af37,
      transparent: true,
      opacity: 0.54,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  pulse.rotation.x = Math.PI / 2;
  pulse.position.y = 0.009;

  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(0.108, 24, 24),
    new THREE.MeshBasicMaterial({
      color: 0xd4af37,
      transparent: true,
      opacity: 0.3,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  glow.position.y = 0.038;

  const dot = new THREE.Mesh(
    new THREE.SphereGeometry(0.036, 18, 18),
    new THREE.MeshBasicMaterial({
      color: 0xfaffb9,
      transparent: true,
      opacity: 0.98,
      blending: THREE.AdditiveBlending,
    }),
  );
  dot.position.y = 0.052;

  group.add(halo, glow, dot, pulse);
  group.userData.halo = halo;
  group.userData.glow = glow;
  group.userData.dot = dot;
  group.userData.pulse = pulse;
  group.visible = false;

  return group;
}

function purchaseLocationKey(location) {
  const countryCode = String(location?.countryCode || location?.code || "").trim().toUpperCase();
  const lat = Number(location?.lat);
  const lon = Number(location?.lon);

  if (!countryCode || !Number.isFinite(lat) || !Number.isFinite(lon)) {
    return "";
  }

  return `${countryCode}:${lat.toFixed(PURCHASE_LOCATION_MATCH_PRECISION)},${lon.toFixed(PURCHASE_LOCATION_MATCH_PRECISION)}`;
}

function isRealPurchaseLocation(visitor) {
  const lat = Number(visitor?.lat);
  const lon = Number(visitor?.lon);
  const countryCode = String(visitor?.countryCode || "").trim().toUpperCase();
  const countryName = String(visitor?.countryName || "").trim();

  return (
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    Math.abs(lat) <= 85 &&
    Math.abs(lon) <= 180 &&
    countryCode.length === 2 &&
    countryCode !== "XX" &&
    countryName.length > 0 &&
    countryName !== "Unknown"
  );
}

function isVerifiedPurchaseLocation(visitor) {
  return isRealPurchaseLocation(visitor) && VERIFIED_PURCHASE_LOCATION_KEYS.has(purchaseLocationKey(visitor));
}

function angularDistanceDegrees(first, second) {
  const firstLat = (Number(first.lat) * Math.PI) / 180;
  const secondLat = (Number(second.lat) * Math.PI) / 180;
  const deltaLat = ((Number(second.lat) - Number(first.lat)) * Math.PI) / 180;
  const deltaLon = ((Number(second.lon) - Number(first.lon)) * Math.PI) / 180;
  const haversine =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(firstLat) * Math.cos(secondLat) * Math.sin(deltaLon / 2) ** 2;

  return (2 * Math.asin(Math.min(1, Math.sqrt(haversine))) * 180) / Math.PI;
}

function purchaseById(purchases, id) {
  return purchases.find((visitor) => visitor.id === id) || null;
}

function nearestAnchorDistance(candidate, anchors) {
  if (!anchors.length) {
    return 180;
  }

  return Math.min(...anchors.map((anchor) => angularDistanceDegrees(candidate, anchor)));
}

class OmbreLiveGlobe {
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector("[data-ombre-live-globe-stage]") || root;
    this.canvas = root.querySelector("[data-ombre-live-globe-canvas]");
    this.status = root.querySelector("[data-ombre-live-globe-status]");
    this.purchaseCallout = root.querySelector("[data-ombre-live-globe-purchase-callout]");
    this.logoUrl = root.dataset.logoUrl || DEFAULT_LOGO_URL;
    this.logoScale = readNumber(root.dataset.logoSize, 1);
    this.rotateSpeed = readNumber(root.dataset.rotateSpeed, 0.6);
    this.purchaseHighlights = [];
    this.highlightedPurchaseIds = [];
    this.recentHighlightedPurchaseIds = [];
    this.visiblePurchases = [];
    this.nextHighlightSlotIndex = 0;
    this.nextPurchaseLocationCursor = 0;
    this.highlightTimer = null;
    this.animationFrame = null;
    this.countryFillGroup = null;
    this.countryBorderGroup = null;
    this.cityLightField = null;
    this.logoDecal = null;

    this.handleResize = this.resize.bind(this);

    try {
      this.initScene();
    } catch {
      this.root.dataset.webglUnavailable = "true";
      return;
    }

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.stage);

    this.resize();
    this.animate();
    this.createLogoDecal();
    this.loadDarkEarthTexture();
    this.loadCountryBorders();
    this.applySnapshot();
    this.startHighlightRotation();
  }

  initScene() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.92;

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x010407, 0.038);
    this.camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    this.camera.position.set(0, 0, 6.2);

    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.065;
    this.controls.enablePan = false;
    this.controls.enableZoom = false;
    this.controls.enableRotate = true;
    this.controls.rotateSpeed = 0.58;
    this.controls.autoRotate = true;
    this.controls.minPolarAngle = 0.22;
    this.controls.maxPolarAngle = Math.PI - 0.22;
    this.setRotateSpeed(this.rotateSpeed);

    this.globeGroup = new THREE.Group();
    this.globeGroup.rotation.y = ((INITIAL_CENTER_LONGITUDE + 90) * Math.PI) / 180;
    this.scene.add(this.globeGroup);

    const ambientLight = new THREE.AmbientLight(0xc8f0ff, 0.52);
    const keyLight = new THREE.DirectionalLight(0xeefaff, 2.25);
    keyLight.position.set(-3.2, 2.4, 4.8);
    const sunLight = new THREE.DirectionalLight(0xd4af37, 1.05);
    sunLight.position.set(3.6, 0.8, 2.8);
    const rimLight = new THREE.DirectionalLight(0xd4af37, 1.35);
    rimLight.position.set(4.2, 0.8, -3.4);
    this.scene.add(ambientLight, keyLight, sunLight, rimLight);

    this.globeMaterial = new THREE.MeshStandardMaterial({
      color: 0x0a0906,
      metalness: 0.34,
      roughness: 0.48,
      emissive: 0x050301,
      emissiveIntensity: 0.1,
    });
    this.globe = new THREE.Mesh(
      new THREE.SphereGeometry(2.08, 128, 128),
      this.globeMaterial,
    );
    this.globeGroup.add(this.globe);

    this.rimGlow = createGlobeRimGlow();
    this.globeGroup.add(this.rimGlow);

    this.purchaseHighlights = Array.from({ length: ACTIVE_PURCHASE_PULSE_COUNT }, (_, index) => {
      const highlight = createPurchaseHighlight();
      highlight.userData.slotIndex = index;
      this.globeGroup.add(highlight);
      return highlight;
    });
  }

  setRotateSpeed(value) {
    const nextSpeed = readNumber(value, this.rotateSpeed);
    this.rotateSpeed = Math.max(0, Math.min(10, nextSpeed));
    this.root.dataset.rotateSpeed = String(this.rotateSpeed.toFixed(2));

    if (this.controls) {
      this.controls.autoRotateSpeed = this.rotateSpeed;
      this.controls.autoRotate = this.rotateSpeed > 0;
    }
  }

  async loadDarkEarthTexture() {
    try {
      const texture = await createDarkEarthTexture();
      const previousTexture = this.globeMaterial.map;
      this.globeMaterial.map = texture;
      this.globeMaterial.color.setHex(0xffffff);
      this.globeMaterial.needsUpdate = true;
      previousTexture?.dispose?.();
    } catch {
      this.globeMaterial.color.setHex(0x090806);
      this.globeMaterial.needsUpdate = true;
    }
  }

  async loadCountryBorders() {
    try {
      const response = await fetch(WORLD_ATLAS_URL, { cache: "force-cache" });

      if (!response.ok) {
        throw new Error(`World atlas request failed: ${response.status}`);
      }

      const topology = await response.json();
      const countryMesh = topojsonMesh(topology, topology.objects.countries);
      const countriesGeojson = topojsonFeature(topology, topology.objects.countries);
      this.countryFillGroup = createCountryFillGroup(countriesGeojson);
      this.countryBorderGroup = createCountryBorderGroup(countryMesh);
      this.cityLightField = createCityLightField(countriesGeojson);
      this.globeGroup.add(this.countryFillGroup);
      this.globeGroup.add(this.countryBorderGroup);
      this.globeGroup.add(this.cityLightField);
      this.root.dataset.countryFillReady = "true";
      this.root.dataset.cityLightCount = String(this.cityLightField.userData.count);
      this.root.dataset.countryBorderCount = String(this.countryBorderGroup.userData.segmentCount);
    } catch {
      this.setStatus("World map detail unavailable");
    }
  }

  async createLogoDecal() {
    try {
      const texture = await createLogoTexture(this.logoUrl);
      const group = new THREE.Group();
      group.add(createLogoDecalGroup(texture, INITIAL_CENTER_LONGITUDE, this.logoScale));
      group.add(createLogoDecalGroup(texture, -INITIAL_CENTER_LONGITUDE, this.logoScale));
      this.globeGroup.add(group);
      this.logoDecal = group;
      this.root.dataset.logoReady = "true";
    } catch {
      this.setStatus("Live globe logo unavailable");
    }
  }

  createDemoPurchasePool() {
    return LOCATIONS.map((location, index) => ({
      id: `demo-purchase-${index}-${location.code}-${location.city.toLowerCase().replace(/\s+/g, "-")}`,
      countryCode: location.code,
      countryName: location.name,
      city: location.city,
      lat: location.lat,
      lon: location.lon,
      message: PURCHASE_MESSAGE,
    }));
  }

  createSeparatedPurchaseVisitors(visitors) {
    const displayVisitors = [];

    for (const visitor of visitors.filter(isVerifiedPurchaseLocation)) {
      if (!displayVisitors.length || nearestAnchorDistance(visitor, displayVisitors) >= PURCHASE_DOT_SEPARATION_DEGREES) {
        displayVisitors.push(visitor);
      }
    }

    return displayVisitors;
  }

  isPurchaseFacingCamera(visitor) {
    const surfaceNormal = toThreeVector(visitor.lat, visitor.lon, 1).normalize();
    const worldNormal = surfaceNormal.applyQuaternion(this.globeGroup.quaternion);
    const cameraNormal = this.camera.position.clone().normalize();

    return worldNormal.dot(cameraNormal) > VISIBLE_PURCHASE_DOT_THRESHOLD;
  }

  rememberHighlightedPurchase(visitor) {
    this.recentHighlightedPurchaseIds = [
      visitor.id,
      ...this.recentHighlightedPurchaseIds.filter((id) => id !== visitor.id),
    ].slice(0, RECENT_HIGHLIGHT_MEMORY);
  }

  highlightedPurchaseAnchors(purchases, extraAnchors = []) {
    const anchorIds = new Set([...this.highlightedPurchaseIds, ...this.recentHighlightedPurchaseIds]);
    const anchors = [...anchorIds]
      .map((id) => purchaseById(purchases, id))
      .filter(Boolean);

    return [...anchors, ...extraAnchors];
  }

  selectSeparatedPurchase(purchases, excludedIds = new Set(), extraAnchors = [], options = {}) {
    const { requireSeparation = false } = options;
    const validPurchases = purchases.filter(isVerifiedPurchaseLocation);

    if (!validPurchases.length) {
      return null;
    }

    const anchors = this.highlightedPurchaseAnchors(validPurchases, extraAnchors);
    const recentIds = new Set(this.recentHighlightedPurchaseIds);
    const freshPurchases = validPurchases.filter((visitor) => {
      return !excludedIds.has(visitor.id) && !this.highlightedPurchaseIds.includes(visitor.id) && !recentIds.has(visitor.id);
    });
    const separatedPurchases = freshPurchases.filter((visitor) => {
      return nearestAnchorDistance(visitor, anchors) >= MIN_HIGHLIGHT_SEPARATION_DEGREES;
    });
    const fallbackPurchases = requireSeparation
      ? []
      : validPurchases.filter((visitor) => {
          return !excludedIds.has(visitor.id) && !this.highlightedPurchaseIds.includes(visitor.id);
        });
    const finalFallbackPurchases = requireSeparation
      ? []
      : validPurchases.filter((visitor) => !excludedIds.has(visitor.id));
    const candidatePool = requireSeparation
      ? separatedPurchases
      : separatedPurchases.length
        ? separatedPurchases
        : freshPurchases.length
          ? freshPurchases
          : fallbackPurchases.length
            ? fallbackPurchases
            : finalFallbackPurchases;

    if (!candidatePool.length) {
      return null;
    }

    return candidatePool
      .map((visitor) => ({
        visitor,
        distance: nearestAnchorDistance(visitor, anchors),
      }))
      .sort((first, second) => {
        return (
          second.distance - first.distance ||
          first.visitor.countryName.localeCompare(second.visitor.countryName) ||
          first.visitor.city.localeCompare(second.visitor.city)
        );
      })[0].visitor;
  }

  chooseHighlightedPurchases(purchases) {
    const validPurchases = purchases.filter(isVerifiedPurchaseLocation);
    const visiblePurchasePool = validPurchases.filter((visitor) => this.isPurchaseFacingCamera(visitor));
    const primaryPool = visiblePurchasePool.length ? visiblePurchasePool : validPurchases;
    const selectedPurchases = [];
    const selectedIds = new Set();
    const selectedCountryCodes = new Set();

    for (let slotIndex = 0; slotIndex < ACTIVE_PURCHASE_PULSE_COUNT && selectedPurchases.length < validPurchases.length; slotIndex += 1) {
      const candidatePool = selectedPurchases.length < primaryPool.length ? primaryPool : validPurchases;
      const uniqueCountryPool = candidatePool.filter((visitor) => !selectedCountryCodes.has(visitor.countryCode));
      const preferredPool = uniqueCountryPool.length ? uniqueCountryPool : candidatePool;
      const selectedPurchase =
        this.selectSeparatedPurchase(preferredPool, selectedIds, selectedPurchases, { requireSeparation: true }) ||
        this.selectSeparatedPurchase(validPurchases, selectedIds, selectedPurchases, { requireSeparation: true }) ||
        this.selectSeparatedPurchase(preferredPool, selectedIds, selectedPurchases) ||
        this.selectSeparatedPurchase(validPurchases, selectedIds, selectedPurchases);

      if (!selectedPurchase) {
        break;
      }

      selectedPurchases.push(selectedPurchase);
      selectedIds.add(selectedPurchase.id);
      selectedCountryCodes.add(selectedPurchase.countryCode);
    }

    return selectedPurchases;
  }

  setHighlightedPurchases(visitors, options = {}) {
    if (!this.purchaseHighlights.length) {
      return;
    }

    const { popCallout = true, changedSlotIndex = 0 } = options;
    const previousIds = this.highlightedPurchaseIds;
    const nextIds = [];
    const nextCountries = [];
    const nextDistances = [];
    const nextCoordinates = [];
    let changedSlot = -1;

    for (const [slotIndex, highlight] of this.purchaseHighlights.entries()) {
      const visitor = visitors[slotIndex];

      if (!visitor) {
        highlight.visible = false;
        continue;
      }

      const previous = purchaseById(this.visiblePurchases, previousIds[slotIndex]);
      const distance = previous && previous.id !== visitor.id ? angularDistanceDegrees(previous, visitor) : 0;
      const isChangedSlot = !previous || previous.id !== visitor.id;

      placePurchaseDot(highlight, visitor);
      highlight.visible = true;
      nextIds.push(visitor.id);
      nextCountries.push(visitor.countryName || visitor.countryCode);
      nextDistances.push(distance.toFixed(1));
      nextCoordinates.push(`${Number(visitor.lat).toFixed(4)},${Number(visitor.lon).toFixed(4)}`);

      if (isChangedSlot) {
        changedSlot = changedSlot === -1 ? slotIndex : changedSlot;
        this.rememberHighlightedPurchase(visitor);
      }
    }

    const calloutSlotIndex = Math.min(Math.max(changedSlot === -1 ? changedSlotIndex : changedSlot, 0), nextIds.length - 1);
    this.highlightedPurchaseIds = nextIds;
    this.root.dataset.highlightedPurchase = nextIds[calloutSlotIndex] || nextIds[0] || "";
    this.root.dataset.highlightedPurchases = nextIds.join(",");
    this.root.dataset.highlightedCountry = nextCountries.join(",");
    this.root.dataset.highlightedDistanceDegrees = nextDistances.join(",");
    this.root.dataset.highlightedCoordinates = nextCoordinates.join("|");
    this.root.dataset.activePurchasePulseCount = String(nextIds.length);
    this.root.dataset.changedPurchaseSlot = nextIds.length ? String(calloutSlotIndex) : "";

    if (popCallout && nextIds.length && changedSlot !== -1) {
      this.showPurchaseCallout();
    }
  }

  showPurchaseCallout() {
    if (!this.purchaseCallout) {
      return;
    }

    this.purchaseCallout.textContent = PURCHASE_MESSAGE;
    this.purchaseCallout.hidden = true;
    this.purchaseCallout.classList.remove("ombre-live-globe__purchase-callout--active");
    void this.purchaseCallout.offsetWidth;
    this.purchaseCallout.hidden = false;
    this.purchaseCallout.classList.add("ombre-live-globe__purchase-callout--active");
  }

  syncHighlightedPurchases(visitors) {
    this.visiblePurchases = visitors.filter(isVerifiedPurchaseLocation);

    if (!this.visiblePurchases.length) {
      this.highlightedPurchaseIds = [];
      this.recentHighlightedPurchaseIds = [];

      this.purchaseHighlights.forEach((highlight) => {
        highlight.visible = false;
      });

      delete this.root.dataset.highlightedPurchase;
      delete this.root.dataset.highlightedPurchases;
      delete this.root.dataset.highlightedCountry;
      delete this.root.dataset.highlightedDistanceDegrees;
      delete this.root.dataset.highlightedCoordinates;
      delete this.root.dataset.activePurchasePulseCount;
      delete this.root.dataset.changedPurchaseSlot;
      this.nextHighlightSlotIndex = 0;
      this.nextPurchaseLocationCursor = 0;

      if (this.purchaseCallout) {
        this.purchaseCallout.hidden = true;
      }

      return;
    }

    const requiredCount = Math.min(ACTIVE_PURCHASE_PULSE_COUNT, this.visiblePurchases.length);
    const existingIds = this.highlightedPurchaseIds.filter((id) => purchaseById(this.visiblePurchases, id));
    const visibleHighlights = this.purchaseHighlights.filter((highlight) => highlight.visible).length;

    if (existingIds.length === requiredCount && visibleHighlights === requiredCount) {
      return;
    }

    this.setHighlightedPurchases(this.chooseHighlightedPurchases(this.visiblePurchases));
    this.nextHighlightSlotIndex = 0;
  }

  chooseReplacementPurchase(slotIndex) {
    const currentPurchases = this.highlightedPurchaseIds
      .map((id) => purchaseById(this.visiblePurchases, id))
      .filter(Boolean);
    const retainedPurchases = currentPurchases.filter((_, index) => index !== slotIndex);
    const currentIds = new Set(currentPurchases.map((visitor) => visitor.id));
    const retainedCountryCodes = new Set(retainedPurchases.map((visitor) => visitor.countryCode));
    const validPurchases = this.visiblePurchases.filter(isVerifiedPurchaseLocation);
    const recentIds = new Set(this.recentHighlightedPurchaseIds);
    const recentCountryCodes = new Set(
      this.recentHighlightedPurchaseIds
        .map((id) => purchaseById(validPurchases, id)?.countryCode)
        .filter(Boolean),
    );

    if (!validPurchases.length) {
      return null;
    }

    const startIndex =
      (this.nextPurchaseLocationCursor + slotIndex * PURCHASE_LOCATION_ROTATION_STEP) % validPurchases.length;
    const pickCandidate = ({
      avoidRecentIds = true,
      avoidRecentCountries = true,
      avoidRetainedCountries = true,
      requireSeparation = true,
    } = {}) => {
      for (let offset = 0; offset < validPurchases.length; offset += 1) {
        const index = (startIndex + offset) % validPurchases.length;
        const candidate = validPurchases[index];
        const hasEnoughSpace =
          !requireSeparation ||
          nearestAnchorDistance(candidate, retainedPurchases) >= MIN_HIGHLIGHT_SEPARATION_DEGREES;

        if (currentIds.has(candidate.id)) {
          continue;
        }

        if (avoidRetainedCountries && retainedCountryCodes.has(candidate.countryCode)) {
          continue;
        }

        if (avoidRecentIds && recentIds.has(candidate.id)) {
          continue;
        }

        if (avoidRecentCountries && recentCountryCodes.has(candidate.countryCode)) {
          continue;
        }

        if (!hasEnoughSpace) {
          continue;
        }

        this.nextPurchaseLocationCursor = (index + PURCHASE_LOCATION_ROTATION_STEP) % validPurchases.length;
        return candidate;
      }

      return null;
    };

    return (
      pickCandidate() ||
      pickCandidate({ avoidRecentCountries: false }) ||
      pickCandidate({ avoidRecentIds: false, avoidRecentCountries: false }) ||
      pickCandidate({ avoidRecentIds: false, avoidRecentCountries: false, avoidRetainedCountries: false }) ||
      pickCandidate({
        avoidRecentIds: false,
        avoidRecentCountries: false,
        avoidRetainedCountries: false,
        requireSeparation: false,
      })
    );
  }

  replaceHighlightedPurchaseSlot(slotIndex) {
    const requiredCount = Math.min(ACTIVE_PURCHASE_PULSE_COUNT, this.visiblePurchases.length);

    if (!requiredCount) {
      return;
    }

    const currentPurchases = this.highlightedPurchaseIds
      .slice(0, requiredCount)
      .map((id) => purchaseById(this.visiblePurchases, id));

    if (currentPurchases.some((visitor) => !visitor)) {
      this.setHighlightedPurchases(this.chooseHighlightedPurchases(this.visiblePurchases));
      this.nextHighlightSlotIndex = 0;
      return;
    }

    const targetSlotIndex = slotIndex % requiredCount;
    const replacement = this.chooseReplacementPurchase(targetSlotIndex);

    if (!replacement || currentPurchases[targetSlotIndex]?.id === replacement.id) {
      this.nextHighlightSlotIndex = (targetSlotIndex + 1) % requiredCount;
      return;
    }

    const nextPurchases = [...currentPurchases];
    nextPurchases[targetSlotIndex] = replacement;
    this.setHighlightedPurchases(nextPurchases, { changedSlotIndex: targetSlotIndex });
    this.nextHighlightSlotIndex = (targetSlotIndex + 1) % requiredCount;
  }

  advanceHighlightedPurchases() {
    if (!this.visiblePurchases.length) {
      return;
    }

    this.replaceHighlightedPurchaseSlot(this.nextHighlightSlotIndex);
  }

  startHighlightRotation() {
    window.clearInterval(this.highlightTimer);
    this.highlightTimer = window.setInterval(() => this.advanceHighlightedPurchases(), HIGHLIGHT_ROTATION_MS);
  }

  applySnapshot() {
    const purchasePool = this.createDemoPurchasePool();
    const displayPurchaseVisitors = this.createSeparatedPurchaseVisitors(purchasePool);

    this.syncHighlightedPurchases(displayPurchaseVisitors);

    if (this.purchaseCallout && this.highlightedPurchaseIds.length) {
      this.purchaseCallout.hidden = false;
    }
  }

  setStatus(label) {
    if (this.status) {
      this.status.textContent = label;
    }
  }

  resize() {
    const width = Math.max(1, this.stage.clientWidth);
    const height = Math.max(1, this.stage.clientHeight);

    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.position.x = 0;
    this.camera.position.y = 0;
    this.camera.position.z = width < 760 ? 6.9 : 7.25;
    this.globeGroup.position.x = 0;
    this.globeGroup.position.y = 0;
    this.camera.updateProjectionMatrix();
  }

  animate(time = 0) {
    this.animationFrame = window.requestAnimationFrame((nextTime) => this.animate(nextTime));
    this.controls.update();

    if (this.cityLightField) {
      this.cityLightField.material.size = 0.032 + Math.sin(time * 0.0018) * 0.0045;
      this.cityLightField.material.opacity = 0.9 + Math.sin(time * 0.0014) * 0.08;
    }

    for (const purchaseHighlight of this.purchaseHighlights) {
      if (!purchaseHighlight.visible) {
        continue;
      }

      const slotOffset = purchaseHighlight.userData.slotIndex * 0.72;
      const pulse = 1 + Math.sin(time * 0.004 + purchaseHighlight.position.x + slotOffset) * 0.22;
      const haloScale = 0.38 + Math.max(0, Math.sin(time * 0.006 + slotOffset)) * 0.24;
      purchaseHighlight.userData.halo.scale.set(haloScale, haloScale, 1);
      purchaseHighlight.userData.halo.material.opacity = 0.38 + Math.max(0, Math.sin(time * 0.006 + slotOffset)) * 0.26;
      purchaseHighlight.userData.glow.scale.setScalar(pulse);
      purchaseHighlight.userData.dot.scale.setScalar(1 + Math.sin(time * 0.006 + slotOffset) * 0.14);
      purchaseHighlight.userData.pulse.scale.setScalar(1 + Math.sin(time * 0.003 + slotOffset) * 0.34);
      purchaseHighlight.userData.pulse.material.opacity = 0.34 + Math.max(0, Math.sin(time * 0.004 + slotOffset)) * 0.28;
    }

    this.renderer.render(this.scene, this.camera);
  }

  destroy() {
    window.cancelAnimationFrame(this.animationFrame);
    window.clearInterval(this.highlightTimer);
    this.resizeObserver?.disconnect();
    this.controls?.dispose();
    this.renderer?.dispose();
    instances.delete(this.root);
  }
}

function initLiveGlobes(scope = document) {
  scope.querySelectorAll("[data-ombre-live-globe]").forEach((root) => {
    if (!instances.has(root)) {
      instances.set(root, new OmbreLiveGlobe(root));
    }
  });
}

function destroyLiveGlobes(scope = document) {
  scope.querySelectorAll("[data-ombre-live-globe]").forEach((root) => {
    instances.get(root)?.destroy();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initLiveGlobes(), { once: true });
} else {
  initLiveGlobes();
}

document.addEventListener("shopify:section:load", (event) => initLiveGlobes(event.target));
document.addEventListener("shopify:section:unload", (event) => destroyLiveGlobes(event.target));
